/**
 * TabGrid - Reusable multi-row tab grid component
 * 
 * Replaces horizontal scrolling tabs with a fully visible grid layout.
 * All tabs are visible at once - no scrolling, no arrows, no hidden features.
 * 
 * Design Goals:
 * - "I can see everything. Nothing is hidden. I am in control."
 * - Calm, trustworthy, accessible
 */

/**
 * TabItem - Individual tab button within the grid
 */
const TabItem = ({ 
  id, 
  label, 
  icon: Icon, 
  colors, 
  isActive, 
  onClick, 
  testIdPrefix = 'tab'
}) => {
  return (
    <button
      data-tab-id={id}
      onClick={() => onClick(id)}
      className={`
        flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
        font-medium text-sm transition-all duration-150 ease-out
        focus:outline-none focus:ring-2 focus:ring-offset-1
        ${isActive 
          ? 'shadow-sm' 
          : 'hover:shadow-sm'
        }
      `}
      style={{
        backgroundColor: isActive ? colors.active : colors.bg,
        color: colors.text,
        border: isActive ? `1.5px solid ${colors.text}35` : '1.5px solid transparent',
        minHeight: '44px',
        focusRingColor: `${colors.text}40`,
      }}
      aria-selected={isActive}
      role="tab"
      data-testid={`${testIdPrefix}-${id}`}
    >
      {/* Icon (optional) */}
      {Icon && (
        <span 
          className="flex items-center justify-center w-6 h-6 rounded-lg flex-shrink-0"
          style={{ 
            backgroundColor: isActive ? `${colors.text}18` : colors.iconBg || `${colors.text}10`,
          }}
        >
          <Icon 
            className="h-3.5 w-3.5" 
            style={{ color: colors.text }}
          />
        </span>
      )}
      
      {/* Active indicator dot */}
      {isActive && (
        <span 
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: colors.text }}
          aria-hidden="true"
        />
      )}
      
      {/* Label */}
      <span className={`truncate ${isActive ? 'font-semibold' : 'font-medium'}`}>
        {label}
      </span>
    </button>
  );
};

/**
 * TabGrid - Grid layout container for tabs
 * 
 * @param {Array} tabs - Array of tab objects: { id, label, icon?, colors }
 * @param {string} activeTab - Currently selected tab ID
 * @param {function} onTabChange - Callback when tab is clicked
 * @param {string} testIdPrefix - Prefix for test IDs (default: 'tab')
 * @param {ReactNode} children - Tab content to render
 */
const TabGrid = ({ 
  tabs, 
  activeTab, 
  onTabChange, 
  testIdPrefix = 'tab',
  children 
}) => {
  // Determine optimal grid columns based on number of tabs
  const getGridClass = () => {
    const count = tabs.length;
    if (count === 3) return 'grid-cols-3'; // Always 3 columns for 3 tabs
    if (count === 4) return 'grid-cols-2 sm:grid-cols-4';
    if (count === 5) return 'grid-cols-3 sm:grid-cols-5';
    if (count === 6) return 'grid-cols-3 sm:grid-cols-6';
    return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6';
  };

  return (
    <div className="w-full">
      {/* Tab Grid Navigation */}
      <div 
        className="mb-6"
        role="tablist"
        aria-label="Tab navigation"
      >
        <div className={`grid gap-2 ${getGridClass()}`}>
          {tabs.map((tab) => (
            <TabItem
              key={tab.id}
              id={tab.id}
              label={tab.label}
              icon={tab.icon}
              colors={tab.colors}
              isActive={activeTab === tab.id}
              onClick={onTabChange}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div role="tabpanel">
        {children}
      </div>
    </div>
  );
};

export { TabGrid, TabItem };
export default TabGrid;
